/**
 * Sezione Estensioni - Per Seller e Buyer
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Puzzle, 
  Boxes, 
  Truck, 
  Building2,
  MapPin,
  Clock,
  Package,
  Route,
  CheckSquare,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';

// Mappa icone per estensione
const EXT_ICONS = {
  ext_stock: Boxes,
  ext_logistics: Truck,
  ext_multisito: Building2
};

// Icone per sotto-moduli logistica
const SUBMODULE_ICONS = {
  tracking: MapPin,
  delivery: Clock,
  carriers: Package,
  routes: Route,
  proof: CheckSquare
};

const ExtensionsSection = ({ 
  user,
  subscriptions,
  features,
  config,
  isExtensionActive,
  isExtensionTrial,
  getTrialDaysRemaining,
  isFeatureActive,
  updateFeatures,
  startTrial,
  extensionsLoading
}) => {
  const navigate = useNavigate();
  const [expandedExt, setExpandedExt] = useState({});
  const [extensionActionLoading, setExtensionActionLoading] = useState(null);
  const [featureLoading, setFeatureLoading] = useState({});

  const handleStartTrial = async (extCode) => {
    setExtensionActionLoading(extCode);
    const result = await startTrial(extCode);
    setExtensionActionLoading(null);
    if (result.success) {
      toast.success('Trial avviato! Hai 14 giorni di prova gratuita.');
    } else {
      toast.error(result.error || 'Errore nell\'avvio del trial');
    }
  };

  const handleFeatureToggle = async (extCode, featureCode, currentValue) => {
    setFeatureLoading({ [extCode]: featureCode });
    
    const extFeatures = features[extCode];
    const newFeatures = {
      logistics: {
        ...extFeatures?.logistics,
        [featureCode]: !currentValue
      }
    };
    
    const result = await updateFeatures(extCode, newFeatures);
    setFeatureLoading({});
    
    if (result.success) {
      toast.success(`${featureCode} ${!currentValue ? 'attivato' : 'disattivato'}`);
    } else {
      toast.error(result.error || 'Errore nell\'aggiornamento');
    }
  };

  if (user?.role !== 'seller' && user?.role !== 'buyer') return null;

  return (
    <Card id="extensions-section" data-testid="extensions-section">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Puzzle className="h-5 w-5" />
          Le tue Estensioni
        </CardTitle>
        <CardDescription>
          Gestisci i moduli aggiuntivi della piattaforma
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {extensionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {config.map(ext => {
              const isActive = isExtensionActive(ext.extension);
              const isTrial = isExtensionTrial(ext.extension);
              const trialDays = getTrialDaysRemaining(ext.extension);
              const ExtIcon = EXT_ICONS[ext.extension] || Puzzle;
              const extFeatures = features[ext.extension];
              const isExpanded = expandedExt[ext.extension];

              return (
                <div 
                  key={ext.extension}
                  className={`p-4 rounded-lg border ${
                    isActive 
                      ? 'border-primary/30 bg-primary/5' 
                      : 'border-border bg-muted/30'
                  }`}
                  data-testid={`extension-${ext.extension}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isActive ? 'bg-primary/10' : 'bg-muted'}`}>
                        <ExtIcon className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{ext.name_it}</span>
                          {isActive && (
                            <Badge variant={isTrial ? 'secondary' : 'default'} className="text-xs">
                              {isTrial ? `Trial (${trialDays}gg)` : 'Attivo'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {ext.description_it}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {!isActive && (
                        <Button
                          size="sm"
                          onClick={() => handleStartTrial(ext.extension)}
                          disabled={extensionActionLoading === ext.extension}
                          data-testid={`start-trial-${ext.extension}`}
                        >
                          {extensionActionLoading === ext.extension ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <PlayCircle className="h-4 w-4 mr-1" />
                              Prova Gratis
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Toggle per espandere sotto-moduli */}
                      {ext.has_submodules && isActive && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setExpandedExt({ ...expandedExt, [ext.extension]: !isExpanded })}
                          data-testid={`expand-${ext.extension}`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Sotto-moduli (se espanso) */}
                  {ext.has_submodules && isActive && isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Sotto-moduli
                      </p>
                      <div className="space-y-2">
                        {ext.submodules?.map(sub => {
                          const SubIcon = SUBMODULE_ICONS[sub.code] || Puzzle;
                          const isSubActive = extFeatures?.logistics?.[sub.code] || false;
                          const isLoading = featureLoading[ext.extension] === sub.code;
                          
                          return (
                            <div 
                              key={sub.code}
                              className="flex items-center justify-between p-2 rounded bg-background/50"
                            >
                              <div className="flex items-center gap-2">
                                <SubIcon className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <span className="text-sm">{sub.name_it}</span>
                                  <p className="text-xs text-muted-foreground">{sub.description_it}</p>
                                </div>
                              </div>
                              <Switch
                                checked={isSubActive}
                                onCheckedChange={() => handleFeatureToggle(ext.extension, sub.code, isSubActive)}
                                disabled={isLoading}
                                data-testid={`feature-${sub.code}-switch`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Link rapido a Zone di Consegna se attivo */}
                      {isFeatureActive('ext_logistics', 'delivery') && (
                        <div className="mt-3 pt-3 border-t">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => navigate('/delivery-zones')}
                            className="w-full"
                            data-testid="delivery-zones-link"
                          >
                            <Truck className="h-4 w-4 mr-2" />
                            Configura Zone di Consegna
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExtensionsSection;
